import React, { ReactNode } from 'react';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { ModalSize, ModalVariant } from './Modal.constants';

export enum DataSet {
  ModalBackButtonTestId = 'modal-back-button',
  ModalBodyTestId = 'modal-body',
  ModalFooterTestId = 'modal-footer',
  ModalHeaderTestId = 'modal-header',
  ModalOverlayId = 'modalId',
  ModalOverlayTestId = 'modal-overlay',
}

export enum ConfirmationPrompt {
  CONFIRM = 'confirm',
  DISCARD = 'discard',
  NONE = 'none',
}

/**
 * There are two ways in which the utility ConfirmModal component can be used.
 * The first is a version that provides the user with a confirm/no confirm choice,
 * and requires requisite props for the confirm button to be passed in. Those props
 * are found in ConfirmModalProps.
 *
 * The second is version that allows for N number of actions by passing in a `confirmButtonsChildren`
 * children React.ReactNode instead. Those props are found in ConfirmModalWithChildrenProps.
 */
type ConfirmModalPropsBase = Omit<ModalProps, 'children'> & {
  /** content displayed in the body of the Confirm modal. */
  bodyContent: ReactNode;
  /** The label for the cancel button in the Confirm modal. Defaults to 'Cancel'. */
  cancelLabel?: ModalFooterProps['cancelLabel'];
  /** Whether or not the cancel button should be displayed. Defaults to true. */
  hasCancel?: ModalFooterProps['hasCancel'];
  /** content displayed in the header of the Confirm modal. */
  headerContent: ReactNode;
};

export type ConfirmModalProps = ConfirmModalPropsBase & {
  /** content displayed on the confirmation CTE. */
  confirmButtonContent: ReactNode;
  /** the variant of the ui/button used for the confirmation CTE. Defaults to BUTTON_VARIANTS.PRIMARY */
  confirmButtonVariant?: BUTTON_VARIANTS;
  /** callback that fires if the confirm button is pressed */
  onConfirm: () => void;
};

export type ConfirmModalWithChildrenProps = ConfirmModalPropsBase & {
  /** children slot to provide the ability for N number of confirm actions */
  children: ReactNode;
};

export type ModalFooterProps = {
  /* Whether cancel button is present in footer */
  hasCancel?: boolean;
  /* Label of the cancel button (default is just Cancel) */
  cancelLabel?: string;
  /* Footer content (typically actionable components) */
  children?: ReactNode;
  /** Modal id */
  id?: string;
  /** Allows for optional override of onClose */
  onClose?: () => void;
  /** Allows for optional override of size */
  size?: ModalSize;
  /** display a message in the footer */
  message?: ReactNode;
};

export type ModalProps = {
  /* Body content (can be components) */
  children: ReactNode;
  /** Modal id */
  id: string;
  /** Whether modal is open, used for accessibility attributes */
  isModalOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /* Modal size */
  size?: ModalSize;
  /* Modal variant */
  variant?: ModalVariant;
  /** Override styles */
  style?: React.CSSProperties;
  /**
   * Whether to render the modal within the body portal.
   * Currently a flagged feature to minimize potential breaking changes,
   * but ideally **all new and existing modals should be using this**.
   */
  withPortal?: boolean;
};

export type ModalConfirmationFooterProps = {
  /** the time it takes for the banner open/close animation to run. defaults to '0.25s' */
  animationTime?: string;
  /** Size of the buttons in the confirmation footer */
  buttonSize?: BUTTON_SIZES;
  children?: React.ReactNode;
  /** Allows for optional override of confirmation banner id */
  id?: string;
  /** Optional callback for cancel action, also controls whether default cancel button is present */
  onCancel?: () => void;
  /** Optional callback for confirm changes action, not applicable if using form tag to submit */
  onConfirm?: () => void;
  /** Optional callback for discard changes action */
  onDiscard?: () => void;
  /** Callback for resume editing action */
  onResume: () => void;
  /** Type of prompt to display, whether confirm, discard, or none */
  promptType: ConfirmationPrompt;
  /** Allows for optional override of size */
  modalSize?: ModalSize;
  /** Allows for optional override of variant */
  variant?: ModalVariant;

  cancelButtonLabel?: string;
  confirmButtonLabel?: string | ReactNode;
  confirmHelpText?: string;
  confirmLabel?: string;
  discardButtonLabel?: string;
  discardHelpText?: string;
  discardLabel?: string;
  resumeButtonLabel?: string;
  /** display a message in the footer */
  message?: ReactNode;
};

export type SimpleModalConfirmationFooterProps = Omit<
  ModalConfirmationFooterProps,
  'promptType' | 'onResume' | 'children' | 'onCancel'
> & {
  actionButtonLabel?: string;
  buttonVariant?: BUTTON_VARIANTS;
  /** Optional callback for resume editing action */
  onResume?: () => void;
  /** Optional callback for onCancel action */
  onCancel?: () => void;
};

export type ModalConfirmationFooterPromptLabelProps = Required<
  Pick<
    ModalConfirmationFooterProps,
    'promptType' | 'confirmLabel' | 'discardLabel' | 'confirmHelpText' | 'discardHelpText'
  >
> & {
  showExitAnimation: boolean;
};

export type ModalConfirmationFooterActionButtonProps = Required<
  Pick<
    ModalConfirmationFooterProps,
    'promptType' | 'id' | 'confirmButtonLabel' | 'discardButtonLabel' | 'onConfirm' | 'onDiscard'
  >
> & {
  showExitAnimation: boolean;
  size?: BUTTON_SIZES;
};
